import { useEffect, useMemo, useState } from 'react';
import { TopMenu } from './components/top-menu';
import { MainMenu } from './components/main-menu';
import { Search } from '../search/search';
import { MobileMenu } from './components/mobile-menu/mobile-menu';
import { Alerts } from '@/isomorphic/components/page/components/alerts';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { InsightsNav } from './components/insights-nav';

export const Header = () => {
    const { pageQuery, findAllBlocksOfType } = useGlobalQueries();
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    // Callback ref instead of useRef so the effect below re-runs when the
    // <header> element actually mounts. With useRef, ref.current going from
    // null → element does NOT trigger a re-render, so the effect would miss
    // the moment the header appears (e.g. when navigating from a headerless
    // page back to a normal one) and --header-height would stay stuck at 0.
    const [headerEl, setHeaderEl] = useState<HTMLElement | null>(null);

    const hasInsightsBlock = useMemo(() => {
        const insightBlock = findAllBlocksOfType('resurs/resursbanken-post');
        const insightsBlock = findAllBlocksOfType('resurs/resursbanken-catalog');
        return insightBlock.length > 0 || insightsBlock.length > 0;
    }, [pageQuery.data]);

    // Expose the real, current sticky-header height as a CSS custom property
    // (`--header-height`) on the document root so other blocks can subtract it
    // from viewport-based heights. The header's height isn't fixed — it changes
    // with:
    //   - whether <Alerts> is rendering anything
    //   - market type (cluster hides <TopMenu>)
    //   - custom market logo sizing (height can differ per market)
    //   - whether the <InsightsNav> is rendered
    // so a static constant (like the previous 80px magic number in
    // video-section.tsx) is always wrong in at least some cases.
    //
    // Consumers use `var(--header-height, 80px)` with a sensible fallback so
    // SSR and the pre-hydration paint still render a reasonable layout.
    useEffect(() => {
        if (pageQuery?.data?.remove_header) {
            document.documentElement.style.setProperty('--header-height', '0px');
            return;
        }

        if (!headerEl || typeof window === 'undefined') return;
        const update = () => {
            document.documentElement.style.setProperty('--header-height', `${headerEl.offsetHeight}px`);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(headerEl);
        return () => ro.disconnect();
    }, [pageQuery?.data?.remove_header, headerEl]);

    if (pageQuery?.data?.remove_header) {
        return null;
    }

    return (
        <header ref={setHeaderEl} className="r-sticky r-top-0 r-z-[1000] r-bg-white">
            <Alerts />
            <div className="r-block lg:r-hidden">
                <MobileMenu />
            </div>
            <div className="r-hidden md:r-block">
                <TopMenu />
                <MainMenu onSearchButtonClick={() => setIsSearchOpen(!isSearchOpen)} />
                <div
                    className={`scrollbar-hide r-fixed r-bottom-0 r-right-0 r-top-0 r-z-50 r-w-[600px] r-transform r-overflow-auto r-bg-white r-transition-all r-ease-in-out ${
                        isSearchOpen ? 'r-translate-x-0' : 'r-translate-x-full'
                    }`}
                >
                    <Search isSearchOpen={isSearchOpen} close={() => setIsSearchOpen(false)} />
                </div>
                {isSearchOpen && (
                    <div
                        className="r-fixed r-inset-0 r-z-40 r-bg-black r-opacity-50 r-transition-all r-ease-in-out"
                        onClick={() => setIsSearchOpen(false)}
                    />
                )}
            </div>
            {hasInsightsBlock ? <InsightsNav /> : null}
        </header>
    );
};
